import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import html2canvas from 'html2canvas'
import { API_ROOT } from '../config'
import VisualLockerLine, { getModuleImage } from '../components/VisualLockerLine'
import { fetchProposalProducts } from '../actions/proposalCatalog'

// ─── Visual line capture helpers (mirrors ProposalConfiguratorView PDF) ───────
const CAPTURE_IMG_H = 400
const CAPTURE_ASPECT = {
  'exterior_VLK06': 909 / 2590, 'exterior_VLM02': 548 / 1558, 'exterior_VLM04': 548 / 1558,
  'exterior_VLM04XL': 771 / 1151, 'exterior_VLM06': 548 / 1559, 'exterior_VLM06XL': 615 / 1349,
  'exterior_VLM10': 468 / 1330, 'exterior_VLM12': 493 / 1399,
  'interior_VLK08': 483 / 1322, 'interior_VLM04': 566 / 1551, 'interior_VLM04XL': 933 / 1370,
  'interior_VLM06': 567 / 1551, 'interior_VLM06XL': 544 / 1301,
  'interior_VLM10': 567 / 1552, 'interior_VLM12': 567 / 1551,
}
const getCaptureImgW = (m, env) => {
  const img = m.image || ''
  if (!img || img === 'qr') return 140
  const key = `${env === 'exterior' ? 'exterior' : 'interior'}_${img}`
  return Math.round(CAPTURE_IMG_H * (CAPTURE_ASPECT[key] || 0.36))
}

// Proxy routes on our own backend — avoids CORS and keeps the Freepik key server-side
const PROXY_BASE = `${API_ROOT}freepik`
const POLL_INTERVAL_MS = 3000

function getAuthHeaders() {
  const token = localStorage.getItem('token') || ''
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

// Compress and resize an image file using canvas before base64 encoding.
// Keeps payload small enough to pass through nginx (typically 10MB limit).
// maxDim: max width or height in pixels. quality: JPEG quality 0-1.
function compressToBase64(file, maxDim = 1536, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round((height / width) * maxDim)
          width = maxDim
        } else {
          width = Math.round((width / height) * maxDim)
          height = maxDim
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      // Use JPEG for compression (even if source was PNG) — Freepik accepts it
      const dataUrl = canvas.toDataURL('image/jpeg', quality)
      resolve(dataUrl.split(',')[1])
    }
    img.onerror = reject
    img.src = url
  })
}

class FreepikGeneratorView extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      lockerFile: null,
      lockerPreview: null,
      bgFile: null,
      bgPreview: null,
      prompt: 'You have two input images: (1) a locker cabinet composition and (2) a background room/space. Your task is to integrate the lockers into the room as a photorealistic scene, as if they were always physically present there.\n\nERGONOMIC SCALE REFERENCE:\nUse the following real-world dimensions to ensure correct proportions in the scene:\n— Locker cabinet height: 204 cm\n— Human scale reference: 175 cm\n— Floor-to-ceiling height: 300 cm\nAll elements must be rendered at a scale consistent with these measurements relative to each other and to the room geometry.\n\nDESIGN PRINCIPLES:\nApply interior design principles to maintain visual consistency throughout the proposal. Analyse image (1) to understand the locker composition (number of modules, widths, colours, finishes) and select the most suitable placement and distribution along the target wall according to the room proportions, traffic flow, and aesthetic balance. The arrangement should look intentional and professionally specified, not arbitrarily placed.\n\nSTEP 1 — WALL ANALYSIS:\nIdentify the target wall where the lockers will be placed. Detect its orientation, angle, and perspective vanishing point. Rotate and keystone-correct the locker composition so its vertical axis and depth align perfectly with the wall plane. Do not modify the locker design, proportions, or finish.\n\nSTEP 2 — OBJECT REMOVAL:\nIdentify any furniture, decor, or objects occupying the locker placement zone directly against the target wall. First, remove them completely to have a clean, coherent wall surface and floor behind where they stood. Only remove objects within the locker footprint zone — leave everything else untouched.\n\nSTEP 3 — LOCKER PLACEMENT:\nPlace the lockers flush against the wall, grounded on the floor. The bottom edge must make full contact with the floor surface with no floating or gap. Apply ambient occlusion along the floor contact line.\n\nSTEP 4 — DEPTH AND OCCLUSION:\nAny room objects outside the locker footprint zone that sit in front of the wall plane (furniture, columns, décor) must remain in place and be composited in front of the lockers at the correct depth layer. The lockers sit against the wall, behind foreground elements.\n\nSTEP 5 — LIGHTING AND SHADOW:\nAnalyze the room\'s dominant light direction, color temperature, and intensity from existing shadows and highlights in the scene. Adapt the locker surface shading, highlights, and ambient occlusion to match this environment. Add a realistic floor shadow beneath the lockers consistent with the room\'s light direction, with natural falloff.\n\nSTEP 6 — FINAL COMPOSITING:\nRemove all compositing artifacts — hard edges, color fringing, unnatural outlines. Match the locker color tone to the room\'s ambient environment. Keep all walls, floor, ceiling, and room geometry completely unchanged outside the locker placement zone. Output a single seamless photorealistic image.\n\nSTEP 7 — ASPECT RATIO EXPANSION:\nThe output canvas has a different aspect ratio than the input images. Do NOT stretch, scale, or distort any element — lockers, people, furniture, or architecture must keep their correct proportions. Instead, extend the scene outward by outpainting: continue the walls, floor, ceiling, and background environment naturally into the new canvas area using content-aware generation that is coherent with the existing scene. The locker composition must remain undistorted and at its correct scale. Do not add any label, text or indicator in the final image.',
      // Composite positioning controls (locker placement on background)
      compX: 50,
      compY: 60,
      compScale: 65,
      compOpacity: 100,
      // Integrate params
      aspectRatio: 'widescreen_16_9',
      // Generation state
      generating: false,
      taskId: null,
      status: null,
      resultUrl: null,
      error: null,
      pollTimer: null,
      countdownTimer: null,
      pollCountdown: 0,
      // Modals
      showPromptModal: false,
      // Visual configurator modal
      showConfigModal: false,
      configLine: [],
      configEnv: 'interior',      // current tab (interior | exterior | pos)
      configBaseEnv: 'interior',  // cabinet env — never becomes 'pos'
      capturingConfig: false,
    }

    this.lockerInputRef = React.createRef()
    this.bgInputRef = React.createRef()
    this.captureRef = React.createRef()
  }

  componentDidMount() {
    if (!this.props.products || this.props.products.length === 0) {
      this.props.fetchProposalProducts()
    }
  }

  componentWillUnmount() {
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer)
    if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
  }

  isRasterImage(file) {
    const supported = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!supported.includes(file.type)) {
      this.setState({ error: `Formato "${file.type || file.name}" no soportado. Usa PNG, JPG o WEBP. SVG no es compatible con la API.` })
      return false
    }
    return true
  }

  handleLockerDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && this.isRasterImage(file)) this.setLockerFile(file)
  }

  handleBgDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && this.isRasterImage(file)) this.setBgFile(file)
  }

  setLockerFile(file) {
    const url = URL.createObjectURL(file)
    this.setState({ lockerFile: file, lockerPreview: url, error: null })
  }

  setBgFile(file) {
    const url = URL.createObjectURL(file)
    this.setState({ bgFile: file, bgPreview: url, error: null })
  }

  clearResult() {
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer)
    if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
    this.setState({ generating: false, taskId: null, status: null, resultUrl: null, error: null, pollTimer: null, countdownTimer: null, pollCountdown: 0 })
  }

  async generate() {
    const { lockerFile, bgFile, prompt, compX, compY, compScale, compOpacity, aspectRatio } = this.state
    if (!lockerFile || !bgFile) {
      return this.setState({ error: 'Por favor sube las dos imágenes antes de generar.' })
    }

    this.clearResult()
    this.setState({ generating: true, status: 'COMPRESSING', error: null })

    try {
      // Load both images from object URLs
      const bgUrl = URL.createObjectURL(bgFile)
      const lockerUrl = URL.createObjectURL(lockerFile)
      const [bgImg, lockerImg] = await Promise.all([
        new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = bgUrl }),
        new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = lockerUrl }),
      ])
      URL.revokeObjectURL(bgUrl)
      URL.revokeObjectURL(lockerUrl)

      // Build composite on a fixed 16:9 canvas — background is letterboxed into the center
      // so the model receives the correct output dimensions and can outpaint the empty areas
      // instead of stretching the source image
      const TARGET_W = 1536
      const TARGET_H = Math.round(TARGET_W * 9 / 16) // 864
      const bgAspect = bgImg.naturalWidth / bgImg.naturalHeight
      const targetAspect = TARGET_W / TARGET_H
      let drawBgW, drawBgH, bgOffsetX, bgOffsetY
      if (bgAspect >= targetAspect) {
        // bg is wider — fit by width, letterbox top/bottom
        drawBgW = TARGET_W
        drawBgH = Math.round(TARGET_W / bgAspect)
        bgOffsetX = 0
        bgOffsetY = Math.round((TARGET_H - drawBgH) / 2)
      } else {
        // bg is taller — fit by height, letterbox left/right
        drawBgH = TARGET_H
        drawBgW = Math.round(TARGET_H * bgAspect)
        bgOffsetX = Math.round((TARGET_W - drawBgW) / 2)
        bgOffsetY = 0
      }
      const canvas = document.createElement('canvas')
      canvas.width = TARGET_W
      canvas.height = TARGET_H
      const ctx = canvas.getContext('2d')
      // Black background for letterbox areas — model will outpaint these naturally
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, TARGET_W, TARGET_H)
      ctx.drawImage(bgImg, bgOffsetX, bgOffsetY, drawBgW, drawBgH)
      // Scale locker relative to the bg draw area (not the full canvas)
      const lockerH = drawBgH * (compScale / 100)
      const lockerW = (lockerImg.naturalWidth / lockerImg.naturalHeight) * lockerH
      const lx = bgOffsetX + (drawBgW * (compX / 100)) - lockerW / 2
      const ly = bgOffsetY + (drawBgH * (compY / 100)) - lockerH / 2
      ctx.globalAlpha = compOpacity / 100
      ctx.drawImage(lockerImg, lx, ly, lockerW, lockerH)
      ctx.globalAlpha = 1

      const compositeB64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1]

      // Compress locker + bg to ~1024px before sending — keeps total payload well under 10MB
      const [lockerB64, bgB64] = await Promise.all([
        compressToBase64(lockerFile, 1024, 0.85),
        compressToBase64(bgFile, 1024, 0.85),
      ])

      const res = await fetch(`${PROXY_BASE}/integrate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          composite: compositeB64,
          lockerImage: lockerB64,
          bgImage: bgB64,
          prompt,
          aspectRatio,
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || err.error || `Error ${res.status}`)
      }

      const data = await res.json()
      const taskId = data.data?.task_id
      if (!taskId) throw new Error('No se recibió task_id de la API.')

      this.setState({ taskId, status: data.data?.status || 'CREATED' })
      this.schedulePoll(taskId)

    } catch (e) {
      this.setState({ generating: false, error: e.message || 'Error desconocido.' })
    }
  }

  schedulePoll(taskId) {
    if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
    const secs = POLL_INTERVAL_MS / 1000
    this.setState({ pollCountdown: secs })
    const countdownTimer = setInterval(() => {
      this.setState(prev => {
        if (prev.pollCountdown <= 1) {
          clearInterval(prev.countdownTimer)
          return { pollCountdown: 0, countdownTimer: null }
        }
        return { pollCountdown: prev.pollCountdown - 1 }
      })
    }, 1000)
    const timer = setTimeout(() => this.poll(taskId), POLL_INTERVAL_MS)
    this.setState({ pollTimer: timer, countdownTimer })
  }

  async poll(taskId) {
    try {
      const res = await fetch(`${PROXY_BASE}/integrate/${taskId}`, {
        headers: getAuthHeaders(),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || `Error ${res.status}`)
      }

      const data = await res.json()
      const { status } = data.data || {}

      this.setState({ status })

      if (status === 'COMPLETED') {
        // Flux Kontext Pro — try multiple common response field patterns
        const d = data.data || {}
        const url = (d.generated && d.generated[0]) || d.result || d.url || (d.output && d.output[0])
        if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
        this.setState({ generating: false, resultUrl: url || null, pollTimer: null, countdownTimer: null, pollCountdown: 0 })
      } else if (status === 'FAILED' || status === 'ERROR') {
        const d = data.data || {}
        const msg = d.error || d.message || d.reason || JSON.stringify(d)
        throw new Error(`Magnific: ${msg}`)
      } else {
        // Still in progress — keep polling
        this.schedulePoll(taskId)
      }
    } catch (e) {
      if (this.state.countdownTimer) clearInterval(this.state.countdownTimer)
      this.setState({ generating: false, error: e.message, pollTimer: null, countdownTimer: null, pollCountdown: 0 })
    }
  }

  async downloadComposite() {
    const { lockerPreview, bgPreview, compX, compY, compScale, compOpacity } = this.state
    if (!lockerPreview || !bgPreview) return
    const [bgImg, lockerImg] = await Promise.all([
      new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = bgPreview }),
      new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = lockerPreview }),
    ])
    const TARGET_W = 1536
    const TARGET_H = Math.round(TARGET_W * 9 / 16)
    const bgAspect = bgImg.naturalWidth / bgImg.naturalHeight
    const targetAspect = TARGET_W / TARGET_H
    let drawBgW, drawBgH, bgOffsetX, bgOffsetY
    if (bgAspect >= targetAspect) {
      drawBgW = TARGET_W; drawBgH = Math.round(TARGET_W / bgAspect)
      bgOffsetX = 0; bgOffsetY = Math.round((TARGET_H - drawBgH) / 2)
    } else {
      drawBgH = TARGET_H; drawBgW = Math.round(TARGET_H * bgAspect)
      bgOffsetX = Math.round((TARGET_W - drawBgW) / 2); bgOffsetY = 0
    }
    const canvas = document.createElement('canvas')
    canvas.width = TARGET_W
    canvas.height = TARGET_H
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, TARGET_W, TARGET_H)
    ctx.drawImage(bgImg, bgOffsetX, bgOffsetY, drawBgW, drawBgH)
    const lockerH = drawBgH * (compScale / 100)
    const lockerW = (lockerImg.naturalWidth / lockerImg.naturalHeight) * lockerH
    const lx = bgOffsetX + (drawBgW * (compX / 100)) - lockerW / 2
    const ly = bgOffsetY + (drawBgH * (compY / 100)) - lockerH / 2
    ctx.globalAlpha = compOpacity / 100
    ctx.drawImage(lockerImg, lx, ly, lockerW, lockerH)
    ctx.globalAlpha = 1
    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'locker-composite.png'; a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }

  renderPollLoader() {
    const { generating, status, pollCountdown } = this.state
    if (!generating || !status || status === 'COMPRESSING' || status === 'COMPLETED') return null
    const secs = POLL_INTERVAL_MS / 1000
    const pct = pollCountdown === 0 ? 100 : Math.round(((secs - pollCountdown) / secs) * 100)
    const isWaiting = pollCountdown > 0
    return (
      <div style={{ marginTop: 14 }}>
        <div style={
          { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 6, fontSize: 12, color: '#6b7280' }
        }>
          <span>
            {isWaiting
              ? <><i className="fas fa-hourglass-half mr-1" style={{ color: '#8b5cf6' }}></i>Próxima consulta en <strong>{pollCountdown}s</strong></>  
              : <><i className="fas fa-sync fa-spin mr-1" style={{ color: '#6366f1' }}></i>Consultando...</>}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {this.renderStatusBadge()}
          </span>
        </div>
        {/* Progress bar */}
        <div style={{
          height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: isWaiting
              ? 'linear-gradient(90deg, #6366f1, #8b5cf6)'
              : 'linear-gradient(90deg, #8b5cf6, #6366f1)',
            borderRadius: 99,
            transition: isWaiting ? 'width 1s linear' : 'width 0.2s ease',
          }} />
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>
          Generación AI puede tardar 30–90 segundos
        </div>
      </div>
    )
  }

  renderDropZone(label, icon, preview, onClick, onDrop, onClear) {
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
          <i className={`fas ${icon} mr-2`}></i>{label}
        </div>
        <div
          onClick={onClick}
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          style={{
            border: preview ? '2px solid #6366f1' : '2px dashed #d1d5db',
            borderRadius: 12,
            background: preview ? '#000' : '#f9fafb',
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'border-color 0.2s',
          }}
        >
          {preview ? (
            <>
              <img
                src={preview}
                alt="preview"
                style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', display: 'block' }}
              />
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onClear() }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,.55)', color: '#fff', border: 'none',
                  borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}
                title="Eliminar"
              >
                <i className="fas fa-times"></i>
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: 36, marginBottom: 10, display: 'block' }}></i>
              <div style={{ fontSize: 13 }}>Arrastra aquí o haz clic para seleccionar</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>PNG, JPG, WEBP — SVG no soportado</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Capture the hidden render div as a PNG and set it as locker image ──────
  async captureAndUseConfig() {
    if (!this.state.configLine.length) return
    this.setState({ capturingConfig: true })
    await new Promise(r => setTimeout(r, 120))
    try {
      const el = this.captureRef.current
      const canvas = await html2canvas(el, {
        scale: 2, useCORS: true, logging: false, backgroundColor: null,
      })
      await new Promise((res, rej) => {
        canvas.toBlob(blob => {
          if (!blob) { rej(new Error('No se pudo capturar la imagen.')); return }
          const file = new File([blob], 'locker-config.png', { type: 'image/png' })
          this.setLockerFile(file)
          res()
        }, 'image/png')
      })
      this.setState({ showConfigModal: false, capturingConfig: false })
    } catch (e) {
      this.setState({ capturingConfig: false, error: e.message })
    }
  }

  // ── Hidden off-screen div captured by html2canvas ───────────────────────────
  renderCaptureDiv() {
    const { configLine, configBaseEnv } = this.state
    return (
      <div style={{ position: 'fixed', left: 0, top: 0, zIndex: -9999, opacity: 0, pointerEvents: 'none' }}>
        <div ref={this.captureRef} style={{
          display: 'inline-flex', alignItems: 'flex-end', background: 'transparent',
        }}>
          {configLine.map((m, idx) => {
            const w = getCaptureImgW(m, configBaseEnv)
            return (
              <div key={m.uid || idx} style={{
                width: w, height: CAPTURE_IMG_H, flexShrink: 0,
                backgroundImage: `url(${getModuleImage(m, configBaseEnv)})`,
                backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center bottom',
              }} />
            )
          })}
        </div>
      </div>
    )
  }

  // ── Full-screen modal with VisualLockerLine ──────────────────────────────────
  renderConfigModal() {
    const { showConfigModal, configLine, configEnv, configBaseEnv, capturingConfig } = this.state
    const products = this.props.products || []
    if (!showConfigModal) return null

    // Lock env tabs once any cabinet is in the line (mirrors ProposalConfiguratorView)
    const envLocked = configLine.some(m => m.category === 'cabinet')

    const handleTabClick = (tab) => {
      if (tab === 'pos') {
        // POS tab: never changes base env or clears the line
        this.setState({ configEnv: 'pos' })
        return
      }
      // Interior / Exterior tab
      if (envLocked && tab !== configBaseEnv) return // blocked
      this.setState({ configEnv: tab, configBaseEnv: tab, configLine: envLocked ? configLine : [] })
    }

    const tabs = [
      { v: 'interior', label: 'Interior', icon: 'fa-building' },
      { v: 'exterior', label: 'Exterior', icon: 'fa-sun' },
      { v: 'pos',      label: 'Puntos de venta', icon: 'fa-cash-register' },
    ]

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#fff', borderRadius: 16,
          width: 'min(96vw, 1280px)', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 24px', borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1f2937' }}>
                <i className="fas fa-cubes mr-2" style={{ color: '#6366f1' }}></i>
                Configurador Visual de Lockers
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                Arrastra módulos del panel lateral para componer la línea. Se capturará como imagen para la integración AI.
              </p>
            </div>
            <button
              onClick={() => this.setState({ showConfigModal: false })}
              style={{
                background: 'none', border: 'none', fontSize: 22,
                cursor: 'pointer', color: '#6b7280', lineHeight: 1, padding: '4px 8px', borderRadius: 6,
              }}
            >×</button>
          </div>

          {/* Env tabs */}
          <div style={{
            padding: '10px 24px 0', flexShrink: 0,
            borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 4, alignItems: 'flex-end',
          }}>
            {tabs.map(({ v, label, icon }) => {
              const isLocked = envLocked && v !== 'pos' && v !== configBaseEnv
              const active = configEnv === v
              return (
                <button key={v}
                  onClick={() => handleTabClick(v)}
                  disabled={isLocked}
                  title={isLocked ? 'Limpia la línea de armarios para cambiar de entorno' : ''}
                  style={{
                    padding: '6px 18px', borderRadius: '8px 8px 0 0', border: 'none',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    fontWeight: 700, fontSize: 12,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    background: active ? '#6366f1' : 'transparent',
                    color: active ? '#fff' : isLocked ? '#c4c9d4' : '#6b7280',
                    opacity: isLocked ? 0.5 : 1,
                    transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                  <i className={`fas ${icon}`}></i>
                  {label}{isLocked ? ' 🔒' : ''}
                </button>
              )
            })}
          </div>

          {/* Configurator body */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
            {products.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 30, display: 'block', marginBottom: 10 }}></i>
                Cargando catálogo de productos...
              </div>
            ) : (
              <VisualLockerLine
                products={products}
                env={configEnv}
                lockedEnv={envLocked ? configBaseEnv : null}
                configLine={configLine}
                onChange={line => this.setState({ configLine: line })}
              />
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '14px 24px', borderTop: '1px solid #e5e7eb', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {configLine.length} módulo{configLine.length !== 1 ? 's' : ''} en la línea
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => this.setState({ showConfigModal: false })}
                style={{
                  padding: '9px 22px', borderRadius: 8, border: '1px solid #d1d5db',
                  background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}
              >Cancelar</button>
              <button
                onClick={() => this.captureAndUseConfig()}
                disabled={configLine.length === 0 || capturingConfig}
                style={{
                  padding: '9px 22px', borderRadius: 8, border: 'none',
                  background: configLine.length > 0 && !capturingConfig
                    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#e5e7eb',
                  color: configLine.length > 0 && !capturingConfig ? '#fff' : '#9ca3af',
                  fontWeight: 700, fontSize: 13,
                  cursor: configLine.length > 0 && !capturingConfig ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {capturingConfig
                  ? <><i className="fas fa-spinner fa-spin"></i>Capturando...</>
                  : <><i className="fas fa-camera"></i>Usar esta configuración</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderStatusBadge() {    const { status } = this.state
    if (!status) return null
    const map = {
      COMPRESSING: ['#6366f1', 'Comprimiendo...'],
      CREATED: ['#6366f1', 'Creado'],
      IN_PROGRESS: ['#f59e0b', 'Generando...'],
      COMPLETED: ['#10b981', 'Completado'],
      FAILED: ['#ef4444', 'Fallido'],
      ERROR: ['#ef4444', 'Error'],
    }
    const [color, label] = map[status] || ['#9ca3af', status]
    return (
      <span style={{
        background: color, color: '#fff', padding: '3px 12px', borderRadius: 20,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
      }}>
        {label}
      </span>
    )
  }

  render() {
    const {
      lockerFile, lockerPreview, bgFile, bgPreview,
      prompt, compX, compY, compScale, compOpacity, aspectRatio,
      generating, status, resultUrl, error,
      showConfigModal, showPromptModal, configLine, configEnv, configBaseEnv, capturingConfig,
    } = this.state

    const canGenerate = !!lockerFile && !!bgFile && !generating

    return (
      <div style={{ maxWidth: '100%', padding: '24px 20px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1f2937', margin: 0 }}>
            <i className="fas fa-magic mr-3" style={{ color: '#6366f1' }}></i>
            Generador de Imágenes AI
          </h1>
          <p style={{ color: '#6b7280', marginTop: 6, marginBottom: 0 }}>
            Posiciona el locker en el fondo y genera la integración realista con Seedream 4.5 Edit (Magnific AI).
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, alignItems: 'start' }}>

          {/* ── Col 1: Image uploads ── */}
          <div>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
              <i className="fas fa-images mr-2" style={{ color: '#6366f1' }}></i>Imágenes de entrada
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Imagen 1: upload OR visual configurator */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                  <i className="fas fa-cube mr-2"></i>Imagen 1 – Configuración de Lockers
                </div>
                {lockerPreview ? (
                  <div style={{
                    border: '2px solid #6366f1', borderRadius: 12, background: '#000',
                    minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <img
                      src={lockerPreview}
                      alt="preview"
                      style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', display: 'block' }}
                    />
                    <button
                      type="button"
                      onClick={() => this.setState({ lockerFile: null, lockerPreview: null, configLine: [] })}
                      style={{
                        position: 'absolute', top: 8, right: 8,
                        background: 'rgba(0,0,0,.55)', color: '#fff', border: 'none',
                        borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                      }}
                      title="Eliminar"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div
                      onClick={() => this.lockerInputRef.current.click()}
                      onDrop={e => this.handleLockerDrop(e)}
                      onDragOver={e => e.preventDefault()}
                      style={{
                        border: '2px dashed #d1d5db', borderRadius: 12, background: '#f9fafb',
                        minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: 'border-color 0.2s',
                      }}
                    >
                      <div style={{ textAlign: 'center', color: '#9ca3af', padding: 16 }}>
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: 28, marginBottom: 6, display: 'block' }}></i>
                        <div style={{ fontSize: 12 }}>Arrastra aquí o haz clic para subir</div>
                        <div style={{ fontSize: 10, marginTop: 2 }}>PNG, JPG, WEBP</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                      <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>o</span>
                      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                    </div>
                    <button
                      type="button"
                      onClick={() => this.setState({ showConfigModal: true })}
                      style={{
                        width: '100%', padding: '10px 0', borderRadius: 10,
                        border: '2px dashed #6366f1', background: 'rgba(99,102,241,0.04)',
                        color: '#6366f1', fontWeight: 700, fontSize: 13,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        transition: 'all 0.15s',
                      }}
                    >
                      <i className="fas fa-cubes"></i>Abrir configurador visual
                    </button>
                  </div>
                )}
              </div>
              <input
                ref={this.lockerInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={e => e.target.files[0] && this.isRasterImage(e.target.files[0]) && this.setLockerFile(e.target.files[0])}
              />

              {this.renderDropZone(
                'Imagen 2 – Espacio / Fondo',
                'fa-image',
                bgPreview,
                () => this.bgInputRef.current.click(),
                e => this.handleBgDrop(e),
                () => this.setState({ bgFile: null, bgPreview: null }),
              )}
              <input
                ref={this.bgInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={e => e.target.files[0] && this.isRasterImage(e.target.files[0]) && this.setBgFile(e.target.files[0])}
              />
            </div>
          </div>

          {/* ── Col 2: Config + Generate ── */}
          <div>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
              <i className="fas fa-sliders-h mr-2" style={{ color: '#6366f1' }}></i>Configuración de generación
            </div>
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
              padding: '16px', marginBottom: 16,
            }}>

              {/* Prompt */}
              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => this.setState({ showPromptModal: true })}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8,
                    border: '1px solid #d1d5db', background: '#f3f4f6',
                    color: '#6b7280', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                    textAlign: 'left',
                  }}
                >
                  <i className="fas fa-file-alt" style={{ color: '#6366f1', flexShrink: 0 }}></i>
                  Ver prompt de generación
                  <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: 10 }}></i>
                </button>
              </div>


              {/* Sliders */}
              {[
                { label: 'Posición horizontal', key: 'compX', value: compX, min: 0, max: 100, step: 1, hint: 'izq → der' },
                { label: 'Posición vertical', key: 'compY', value: compY, min: 0, max: 100, step: 1, hint: 'arriba → abajo' },
                { label: 'Tamaño del locker', key: 'compScale', value: compScale, min: 3, max: 100, step: 1, hint: '% del alto del fondo' },
              ].map(({ label, key, value, min, max, step, hint }) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span>{label} <span style={{ fontWeight: 400, color: '#9ca3af' }}>({hint})</span></span>
                    <strong style={{ color: '#374151' }}>{value}</strong>
                  </label>
                  <input
                    type="range" min={min} max={max} step={step}
                    value={value}
                    onChange={e => this.setState({ [key]: Number(e.target.value) })}
                    style={{ width: '100%' }}
                  />
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                padding: '10px 14px', marginBottom: 12, color: '#b91c1c', fontSize: 13,
              }}>
                <i className="fas fa-exclamation-triangle mr-2"></i>{error}
              </div>
            )}

            {/* Download composite — free, instant */}
            <button
              type="button"
              onClick={() => this.downloadComposite()}
              disabled={!lockerPreview || !bgPreview}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 10, marginBottom: 8,
                border: '2px solid #10b981', background: 'transparent',
                color: lockerPreview && bgPreview ? '#059669' : '#9ca3af',
                fontWeight: 700, fontSize: 13,
                cursor: lockerPreview && bgPreview ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <i className="fas fa-download"></i>Descargar composite (gratis)
            </button>

            {/* Generate button — Flux Kontext Pro */}
            <button
              type="button"
              onClick={() => this.generate()}
              disabled={!canGenerate}
              style={{
                width: '100%',
                padding: '13px 0',
                borderRadius: 10,
                border: 'none',
                background: canGenerate
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : '#e5e7eb',
                color: canGenerate ? '#fff' : '#9ca3af',
                fontWeight: 700,
                fontSize: 15,
                cursor: canGenerate ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              {generating ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Refinando... {this.renderStatusBadge()}
                </>
              ) : (
                <>
                  <i className="fas fa-magic"></i>
                  Integrar con AI · ~€0.05
                </>
              )}
            </button>

            {/* Polling loader */}
            {this.renderPollLoader()}
          </div>

          {/* ── Col 3: Live preview / AI Result ── */}
          <div>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
              <i className={`fas ${resultUrl ? 'fa-star' : 'fa-eye'} mr-2`} style={{ color: '#6366f1' }}></i>
              {resultUrl ? 'Resultado Integración AI' : 'Vista previa del composite'}
            </div>
            <div style={{
              border: `1px solid ${resultUrl ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 16,
              overflow: 'hidden', background: '#111',
            }}>
              {resultUrl ? (
                <>
                  <div style={{
                    padding: '10px 14px', background: '#1a1a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#d1d5db' }}>Seedream 4.5 Edit</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => this.setState({ resultUrl: null })}
                        style={{ background: 'transparent', border: '1px solid #4b5563', color: '#9ca3af', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>
                        ← composite
                      </button>
                      <a href={resultUrl} download="integrated-result.jpg" target="_blank" rel="noopener noreferrer"
                        style={{ background: '#10b981', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                        <i className="fas fa-download mr-1"></i>Descargar
                      </a>
                    </div>
                  </div>
                  <img src={resultUrl} alt="Resultado AI" style={{ width: '100%', display: 'block' }} />
                </>
              ) : generating ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 340 }}>
                  <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                    <i className="fas fa-magic fa-spin" style={{ fontSize: 40, color: '#8b5cf6', display: 'block', marginBottom: 12 }}></i>
                    <div style={{ fontWeight: 600, color: '#6b7280' }}>Integrando imágenes con AI...</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>Puede tardar 30–90 segundos</div>
                  </div>
                </div>
              ) : bgPreview ? (
                <div style={{ position: 'relative', width: '100%' }}>
                  <img src={bgPreview} alt="fondo" style={{ width: '100%', display: 'block' }} />
                  {lockerPreview && (
                    <img
                      src={lockerPreview}
                      alt="locker"
                      style={{
                        position: 'absolute',
                        left: `${compX}%`,
                        top: `${compY}%`,
                        height: `${compScale}%`,
                        width: 'auto',
                        transform: 'translate(-50%, -50%)',
                        opacity: compOpacity / 100,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 340 }}>
                  <div style={{ textAlign: 'center', color: '#4b5563', padding: 32 }}>
                    <i className="fas fa-image" style={{ fontSize: 48, display: 'block', marginBottom: 12 }}></i>
                    <div style={{ fontSize: 13 }}>Sube el fondo para ver la vista previa</div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Prompt modal */}
        {showPromptModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#fff', borderRadius: 16, width: 'min(92vw, 680px)',
              maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{
                padding: '16px 24px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
              }}>
                <span style={{ fontWeight: 800, fontSize: 15, color: '#1f2937' }}>
                  <i className="fas fa-file-alt mr-2" style={{ color: '#6366f1' }}></i>
                  Prompt de generación AI
                </span>
                <button
                  onClick={() => this.setState({ showPromptModal: false })}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}
                >×</button>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
                <pre style={{
                  margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  fontSize: 12, lineHeight: 1.7, color: '#374151',
                  fontFamily: "'Segoe UI', system-ui, sans-serif",
                }}>{prompt}</pre>
              </div>
              <div style={{ padding: '12px 24px', borderTop: '1px solid #e5e7eb', flexShrink: 0, textAlign: 'right' }}>
                <button
                  onClick={() => this.setState({ showPromptModal: false })}
                  style={{
                    padding: '8px 22px', borderRadius: 8, border: 'none',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}
                >Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {/* Visual configurator modal */}
        {this.renderConfigModal()}

        {/* Hidden off-screen div for html2canvas capture */}
        {this.renderCaptureDiv()}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  products: state.proposalCatalog ? state.proposalCatalog.products : null,
})
const mapDispatchToProps = dispatch => bindActionCreators({ fetchProposalProducts }, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(FreepikGeneratorView)
